import {
  FlairLocalStorageKey,
  useFlairLocalStorage,
} from '../../../hooks/useFlairLocalStorage';
import {
  ExtraFieldColumnDef,
  TimeSheetColumnSetting,
  defaultColumns,
  timeSheetColumnIds,
} from './types';

type ReturnValue = [
  TimeSheetColumnSetting[],
  (value: TimeSheetColumnSetting[]) => void,
];

export const useTimeSheetColumnSettings = (
  storageKey: Extract<
    FlairLocalStorageKey,
    'timeSheets_columns' | 'employee_timeSheets_columns'
  >,
  supportedColumns: Set<string>,
  extraColumns: ExtraFieldColumnDef[] = [],
  // While extra-field metadata is still loading, `supportedColumns` does not yet
  // contain the dynamic ids. Keep stored settings intact so a save in that
  // window can't silently drop a manager's enabled extra columns.
  extraColumnsLoading = false,
): ReturnValue => {
  const [timeSheetColumnsFromStorage, setTimeSheetColumnsFromStorage] =
    useFlairLocalStorage(storageKey, []);

  const verifiedColumnsFromStorage = timeSheetColumnsFromStorage.filter(
    (col) => extraColumnsLoading || supportedColumns.has(col.id),
  );
  const columnsInStorage: Set<string> = verifiedColumnsFromStorage.reduce(
    (resultSet, curStoredItem) => resultSet.add(curStoredItem.id),
    new Set<string>(),
  );
  const defaultColumnsNotExistentInStorage = timeSheetColumnIds
    .filter(
      (colId) => !columnsInStorage.has(colId) && supportedColumns.has(colId),
    )
    .map((id) => ({
      id,
      checked: defaultColumns[id],
    }));
  const extraColumnsNotExistentInStorage = extraColumns
    .filter(
      (col) => !columnsInStorage.has(col.id) && supportedColumns.has(col.id),
    )
    .map((col) => ({
      id: col.id,
      checked: col.defaultChecked,
    }));
  const timeSheetColumnSettings: TimeSheetColumnSetting[] = [
    ...verifiedColumnsFromStorage,
    ...defaultColumnsNotExistentInStorage,
    ...extraColumnsNotExistentInStorage,
  ];

  return [timeSheetColumnSettings, setTimeSheetColumnsFromStorage];
};
